import { clsx } from "clsx";
import { useState, ChangeEvent } from "react";

import { Icon } from "@smart/itops-sb-design-system-dom";
import { v4 } from "@smart/itops-utils-basic";
import { useLoadTeam } from "@smart/manage-gql-client-dom";
import { useAssetUpload, useUser } from "@smart/manage-hooks-dom";

import { LogoDisplay } from "./logo-display";

const MAX_FILE_SIZE_MB = 1;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const SUPPORTED_FORMATS = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/svg+xml",
];

const getFileExtension = (file: File): string => {
  const fileNameParts = file.name.split(".");
  if (fileNameParts.length > 1) {
    const extension = fileNameParts.pop()?.toLowerCase();
    if (extension) return extension;
  }

  const mimeToExtension: Record<string, string> = {
    "image/png": "png",
    "image/jpeg": "jpg",
    "image/jpg": "jpg",
    "image/svg+xml": "svg",
  };

  return mimeToExtension[file.type] || "";
};

type LogoUploadProps = {
  url: string | null | undefined;
  onUpdate: (url: string) => void;
  uploading: boolean;
  setUploading: (uploading: boolean) => void;
};

export const LogoUpload = ({
  url,
  onUpdate,
  uploading,
  setUploading,
}: LogoUploadProps) => {
  const { uploadAsset } = useAssetUpload();
  const { token } = useUser();
  const { result: team } = useLoadTeam({});
  const [error, setError] = useState<string | undefined>();

  const fileInputId = "brand-logo-upload-input";
  const firmName = team?.values.name || "";

  const handleFileSelect = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!SUPPORTED_FORMATS.includes(file.type)) {
      setError(`Unsupported file type. Please use a PNG, JPG or SVG file.`);
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(
        `File size exceeds ${MAX_FILE_SIZE_MB}MB limit. Please use a smaller file.`,
      );
      return;
    }

    setError(undefined);
    setUploading(true);

    try {
      const extension = getFileExtension(file);
      const fileExtension = extension ? `.${extension}` : "";
      const fileName = `brand-logo/logo-${v4()}${fileExtension}`;

      const downloadUrl = await uploadAsset({
        key: fileName,
        file,
        token,
      });

      onUpdate(downloadUrl);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to upload logo. Please try again.",
      );
    } finally {
      setUploading(false);
      // Reset file input
      const fileInput = document.getElementById(
        fileInputId,
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = "";
      }
    }
  };

  return (
    <div className="flex flex-col gap-small">
      <p className="text-caption text-neutral-600">
        To ensure best display, use a square image (PNG, JPG or SVG) no larger
        than {MAX_FILE_SIZE_MB}MB.
      </p>
      <div className="flex gap-small">
        <div className="flex flex-col gap-small">
          <div className="h-[8.3rem] w-[8.3rem]">
            <LogoDisplay
              error={!!error}
              url={url || team?.values.picture}
              firmName={firmName}
              loading={uploading}
            />
          </div>
          <div className="flex flex-col gap-small items-center justify-center">
            <label
              htmlFor={fileInputId}
              className={clsx(
                "text-ui font-semibold",
                uploading
                  ? "text-neutral-400 pointer-events-none"
                  : "text-blue-560 cursor-pointer",
              )}
            >
              <span>Replace</span>
            </label>
          </div>
          <input
            id={fileInputId}
            type="file"
            accept={SUPPORTED_FORMATS.join(",")}
            onChange={handleFileSelect}
            className="hidden"
            aria-label="Logo file input"
          />
        </div>
        {error && (
          <div className="flex gap-small text-caption text-red-550">
            <Icon
              name="regularCircleExclamation"
              className="mt-1 w-[1rem] h-[1rem]"
            />
            <p>{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};
